'use client'

/**
 * THE ONE CONSENT SWITCH for every analytics surface (PostHog, Google Analytics, Sentry replay).
 *
 * Three states, not two: `undecided` (no banner answer yet) must behave exactly like `denied` for
 * capture — the 2026-09 observability review found autocapture, pageviews and identify() running
 * before any consent existed, while the privacy page said no analytics cookies are used. Nothing
 * may capture until the stored value is exactly '1'.
 *
 * Storage: 'mycio_analytics_consent' = '1' (accepted) | '0' (declined) | absent (undecided).
 * The key is NOT the legacy 'mycio_cookie_ok': that value was written by an acknowledge-only
 * banner whose single button was OK — an acknowledgement of a notice, not consent to analytics —
 * so a legacy '1' must read as UNDECIDED and the two-button banner must ask again. A DECLINED '0'
 * is truthy as a string, which is why every consumer must go through analyticsConsentGranted()
 * and never truthiness on the raw value.
 */
export const CONSENT_KEY = 'mycio_analytics_consent'
export const CONSENT_EVENT = 'mycio:cookie-consent'

export type ConsentState = 'granted' | 'denied' | 'undecided'

export function analyticsConsent(): ConsentState {
  if (typeof window === 'undefined') return 'undecided'
  try {
    const v = localStorage.getItem(CONSENT_KEY)
    return v === '1' ? 'granted' : v === '0' ? 'denied' : 'undecided'
  } catch {
    return 'undecided' // localStorage blocked — treat as no consent
  }
}

export function analyticsConsentGranted(): boolean {
  return analyticsConsent() === 'granted'
}

export function recordConsent(granted: boolean): void {
  try {
    localStorage.setItem(CONSENT_KEY, granted ? '1' : '0')
  } catch {
    /* nothing to persist to — the session stays undecided, i.e. no capture */
  }
  try {
    window.dispatchEvent(new Event(CONSENT_EVENT))
  } catch {
    /* ignore */
  }
}
